import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, UploadSimple, Camera, Trash, PaperPlaneTilt, CheckCircle, XCircle, Coins, FilePdf, Warning, CopySimple, ArrowUUpLeft } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DatePicker } from "./DatePicker";
import { StatusPill } from "./StatusPill";
import { api, errMsg, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";

const CATEGORIES = ["Travel", "Meals", "Lodging", "Transport", "Office Supplies", "Software", "Equipment", "Entertainment", "Utilities", "Other"];
const empty = { vendor: "", date: "", amount: 0, tax: 0, currency: "USD", category: "Other", tags: [], client: "", notes: "", rut: "", doc_number: "", receipts: [] };

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const ReceiptThumb = ({ receipt }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let blobUrl;
    if (receipt.content_type === "application/pdf") return;
    api.get(`/files/${receipt.storage_path}`, { responseType: "blob" }).then((r) => {
      blobUrl = URL.createObjectURL(r.data);
      setUrl(blobUrl);
    }).catch(() => {});
    return () => blobUrl && URL.revokeObjectURL(blobUrl);
  }, [receipt.storage_path, receipt.content_type]);
  if (receipt.content_type === "application/pdf")
    return (
      <div className="aspect-[3/4] border border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1 text-slate-400">
        <FilePdf size={28} />
        <span className="text-[10px] px-1 truncate max-w-full">{receipt.filename}</span>
      </div>
    );
  return (
    <div className="aspect-[3/4] border border-slate-200 overflow-hidden bg-slate-50">
      {url ? <img src={url} alt={receipt.filename} className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse bg-slate-100" />}
    </div>
  );
};

export const ExpensePanel = ({ expense, workspace, onClose, onChanged }) => {
  const { user } = useAuth();
  const { t } = useT();
  const isNew = !expense?.id;
  const [form, setForm] = useState(isNew ? { ...empty, currency: workspace?.currency || "USD" } : { ...empty, ...expense });
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [duplicates, setDuplicates] = useState([]);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const isOwner = isNew || expense?.user_id === user?.id;
  const editable = isOwner && (isNew || ["draft", "rejected"].includes(expense?.status));
  const isManager = ["admin", "manager"].includes(user?.role);
  const isTeam = workspace?.type === "team";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Duplicate detection for managers reviewing team expenses
  useEffect(() => {
    if (!isNew && isManager && isTeam && expense?.id) {
      api.get(`/expenses/${expense.id}`).then((r) => setDuplicates(r.data.duplicates || [])).catch(() => {});
    }
  }, [isNew, isManager, isTeam, expense?.id]);

  const missing = editable ? [!form.rut && t("md_rut"), !form.doc_number && t("md_doc"), !form.date && t("md_date")].filter(Boolean) : [];

  const handleFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setExtracting(true);
    try {
      const { data } = await api.post("/receipts/extract", fd);
      setForm((f) => ({
        ...f,
        receipts: [...(f.receipts || []), data.receipt],
        ...(data.extracted ? {
          vendor: f.vendor || data.extracted.vendor,
          date: f.date || data.extracted.date,
          amount: f.amount || data.extracted.amount,
          tax: f.tax || data.extracted.tax,
          currency: data.extracted.currency || f.currency,
          category: f.category !== "Other" ? f.category : data.extracted.category,
          rut: f.rut || data.extracted.rut,
          doc_number: f.doc_number || data.extracted.doc_number,
        } : {}),
      }));
      toast.success(data.extracted ? t("toast_scanned") : t("toast_uploadedNoAI"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExtracting(false);
    }
  };

  const save = async (thenSubmit = false) => {
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0, tax: parseFloat(form.tax) || 0, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      let id = expense?.id;
      if (isNew) {
        const { data } = await api.post("/expenses", payload);
        id = data.id;
      } else if (editable) {
        await api.put(`/expenses/${id}`, payload);
      }
      if (thenSubmit) {
        const r = await api.post(`/expenses/${id}/submit`);
        toast.success(r.data.auto_approved ? t("toast_autoApproved") : t("toast_submitted"));
        if (missing.length > 0) toast.warning(`${t("missingDataWarn")}: ${missing.join(", ")}`);
      } else {
        toast.success(t("toast_saved"));
      }
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const act = async (action, actionComment) => {
    setSaving(true);
    try {
      await api.post(`/expenses/${expense.id}/${action}`, { comment: actionComment ?? comment });
      toast.success(action === "approve" ? t("toast_approved") : action === "reject" ? t("toast_rejected") : t("toast_reimbursed"));
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const returnDuplicate = async () => {
    setSaving(true);
    try {
      await api.post(`/expenses/${expense.id}/reject`, { comment: t("dup_reason") });
      toast.success(t("toast_returnedDup"));
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const cancelSubmission = async () => {
    setSaving(true);
    try {
      await api.post(`/expenses/${expense.id}/cancel`);
      toast.success(t("toast_cancelledExp"));
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/expenses/${expense.id}`);
      toast.success(t("toast_deleted"));
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/10 backdrop-blur-sm" onClick={onClose} data-testid="panel-backdrop" />
      <div className="absolute inset-y-0 right-0 w-full sm:w-[520px] lg:w-[40vw] bg-white border-l border-slate-200 shadow-[0_0_40px_rgba(0,0,0,0.08)] flex flex-col panel-slide" data-testid="expense-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-lg font-semibold text-slate-900">{isNew ? t("newExpenseTitle") : form.vendor || t("expense")}</h2>
            {!isNew && isTeam && <StatusPill status={expense.status} />}
          </div>
          <button onClick={onClose} data-testid="close-panel-btn" className="text-slate-400 hover:text-slate-900 transition-colors duration-150"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {isManager && isTeam && duplicates.length > 0 && (
            <div className="border border-orange-300 bg-orange-50 p-4 space-y-2" data-testid="duplicate-alert">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-orange-700 flex items-center gap-1.5"><CopySimple size={14} /> {t("dup_alert")}</p>
              <p className="text-sm text-orange-800">{t("dup_body")}</p>
              <div className="space-y-1">
                {duplicates.map((d) => (
                  <p key={d.id} className="text-xs font-mono text-orange-700">
                    {d.date || "—"} · {d.vendor || t("untitled")} · {fmtMoney(d.amount, d.currency)} · {t(`st_${d.status}`)} · {t("dup_by")} {d.user_name}{d.doc_number ? ` · N° ${d.doc_number}` : ""}
                  </p>
                ))}
              </div>
              {expense.status === "submitted" && expense.user_id !== user.id && (
                <Button data-testid="return-duplicate-btn" onClick={returnDuplicate} disabled={saving} className="rounded-none w-full bg-orange-600 hover:bg-orange-700 mt-1">
                  <ArrowUUpLeft size={16} className="mr-1.5" /> {t("returnDuplicate")}
                </Button>
              )}
            </div>
          )}

          {editable && (
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="receipt-file-input" />
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="receipt-camera-input" />
              <button
                data-testid="upload-receipt-btn"
                onClick={() => fileRef.current?.click()}
                disabled={extracting}
                className="w-full border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:-translate-y-0.5 transition-[background-color,transform] duration-150 py-6 flex flex-col items-center gap-2 text-slate-500 relative overflow-hidden"
              >
                {extracting && <div className="scanline" />}
                <UploadSimple size={22} />
                <span className="text-sm font-medium">{extracting ? t("scanning") : t("uploadCta")}</span>
                <span className="text-[11px] text-slate-400">{t("uploadHint")}</span>
              </button>
              <button
                data-testid="camera-receipt-btn"
                onClick={() => cameraRef.current?.click()}
                disabled={extracting}
                className="w-full border border-slate-200 bg-white hover:bg-slate-50 transition-colors duration-150 py-2.5 flex items-center justify-center gap-2 text-sm font-medium text-slate-600"
              >
                <Camera size={17} /> {t("takePhoto")}
              </button>
            </div>
          )}

          {form.receipts?.length > 0 && (
            <div className="grid grid-cols-3 gap-3" data-testid="receipt-thumbnails">
              {form.receipts.map((r, i) => (
                <div key={i} className="relative group">
                  <ReceiptThumb receipt={r} />
                  {editable && (
                    <button data-testid={`remove-receipt-${i}`} onClick={() => set("receipts", form.receipts.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white border border-slate-200 p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {missing.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2.5" data-testid="missing-data-warning">
              <Warning size={17} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">{t("missingDataWarn")}: <b>{missing.join(", ")}</b>.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field label={t("vendor")}><Input data-testid="vendor-input" value={form.vendor} disabled={!editable} onChange={(e) => set("vendor", e.target.value)} placeholder={t("vendorPh")} className="rounded-none" /></Field>
            </div>
            <Field label={t("rutLabel")}><Input data-testid="rut-input" value={form.rut} disabled={!editable} onChange={(e) => set("rut", e.target.value)} placeholder={t("rutPh")} className="rounded-none font-mono" /></Field>
            <Field label={t("docNumberLabel")}><Input data-testid="doc-number-input" value={form.doc_number} disabled={!editable} onChange={(e) => set("doc_number", e.target.value)} placeholder={t("docNumberPh")} className="rounded-none font-mono" /></Field>
            <Field label={t("date")}><DatePicker testId="date-input" value={form.date} disabled={!editable} onChange={(v) => set("date", v)} /></Field>
            <Field label={t("category")}>
              <Select value={form.category} onValueChange={(v) => set("category", v)} disabled={!editable}>
                <SelectTrigger data-testid="category-select" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("amount")}><Input data-testid="amount-input" type="number" step="0.01" value={form.amount} disabled={!editable} onChange={(e) => set("amount", e.target.value)} className="rounded-none font-mono" /></Field>
            <Field label={t("tax")}><Input data-testid="tax-input" type="number" step="0.01" value={form.tax} disabled={!editable} onChange={(e) => set("tax", e.target.value)} className="rounded-none font-mono" /></Field>
            <Field label={t("currency")}><Input data-testid="currency-input" value={form.currency} disabled={!editable} onChange={(e) => set("currency", e.target.value.toUpperCase())} maxLength={3} className="rounded-none font-mono uppercase" /></Field>
            {workspace?.type === "freelancer" && (
              <Field label={t("client")}><Input data-testid="client-input" value={form.client} disabled={!editable} onChange={(e) => set("client", e.target.value)} placeholder={t("clientPh")} className="rounded-none" /></Field>
            )}
            <div className="col-span-2">
              <Field label={t("tagsLabel")}><Input data-testid="tags-input" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} disabled={!editable} onChange={(e) => set("tags", e.target.value)} placeholder={t("tagsPh")} className="rounded-none" /></Field>
            </div>
            <div className="col-span-2">
              <Field label={t("notes")}><Textarea data-testid="notes-input" value={form.notes} disabled={!editable} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-none" /></Field>
            </div>
          </div>

          {!isNew && isManager && isTeam && expense.status === "submitted" && expense.user_id !== user.id && (
            <div className="border border-slate-200 p-4 space-y-3 bg-slate-50">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{t("managerReview")}</p>
              <Textarea data-testid="review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder={t("commentPh")} className="rounded-none bg-white" />
              <div className="flex gap-2">
                <Button data-testid="approve-btn" onClick={() => act("approve")} disabled={saving} className="rounded-none bg-emerald-600 hover:bg-emerald-700 flex-1"><CheckCircle size={16} className="mr-1" /> {t("approve")}</Button>
                <Button data-testid="reject-btn" onClick={() => act("reject")} disabled={saving} variant="destructive" className="rounded-none flex-1"><XCircle size={16} className="mr-1" /> {t("reject")}</Button>
              </div>
            </div>
          )}
          {!isNew && isManager && isTeam && expense.status === "approved" && (
            <Button data-testid="reimburse-btn" onClick={() => act("reimburse")} disabled={saving} className="rounded-none w-full bg-indigo-600 hover:bg-indigo-700"><Coins size={16} className="mr-1" /> {t("markReimbursed")}</Button>
          )}

          {!isNew && isOwner && expense.status === "submitted" && (
            <Button data-testid="cancel-submission-btn" onClick={cancelSubmission} disabled={saving} variant="outline" className="rounded-none w-full border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-300">
              <ArrowUUpLeft size={16} className="mr-1.5" /> {t("cancelSubmission")}
            </Button>
          )}

          {!isNew && expense.audit?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">{t("auditTrail")}</p>
              <div className="border border-slate-200 divide-y divide-slate-100" data-testid="audit-trail">
                {[...expense.audit].reverse().map((a, i) => (
                  <div key={i} className="px-3 py-2 text-xs text-slate-600 flex justify-between gap-2">
                    <span><span className="font-semibold capitalize">{a.action}</span> {t("by")} {a.by}{a.comment ? ` — "${a.comment}"` : ""}</span>
                    <span className="font-mono text-slate-400 whitespace-nowrap">{new Date(a.at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-4 flex items-center gap-2">
          {editable && (
            <>
              <Button data-testid="save-expense-btn" onClick={() => save(false)} disabled={saving} variant="outline" className="rounded-none flex-1">{isTeam ? t("saveDraft") : t("save")}</Button>
              <Button data-testid="submit-expense-btn" onClick={() => save(true)} disabled={saving} className="rounded-none flex-1 bg-slate-900 hover:bg-slate-700">
                <PaperPlaneTilt size={16} className="mr-1" /> {t("submit")}
              </Button>
            </>
          )}
          {!isNew && isOwner && ["draft", "rejected"].includes(expense.status) && (
            <Button data-testid="delete-expense-btn" onClick={del} variant="ghost" className="rounded-none text-red-500 hover:text-red-700 hover:bg-red-50"><Trash size={16} /></Button>
          )}
          {!isNew && !editable && !isManager && <p className="text-xs text-slate-400">{t("total")}: <span className="font-mono font-semibold text-slate-700">{fmtMoney(form.amount, form.currency)}</span></p>}
        </div>
      </div>
    </div>
  );
};
